import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ContentLoader } from "../../Utility/Loader";
import ListPaginationBar from "../../Utility/Pagination/ListPaginationBar";
import { WORK_ITEMS_LIST_PAGE_SIZE_STORAGE_KEY } from "../../Utility/Pagination/listPaginationConstants";
import { useListPageSize } from "../../hooks/useListPageSize";
import { useSorting } from "../../Utility/Sorting";
import "../../Pages/Clients/clients.css";
import CreateModal from "./CreateModal/CreateModal";
import CreateTaskForm from "./CreateModal/CreateTaskForm";
import DeleteConfirmModal from "../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import WorkItemsFilterBar from "./WorkItemsFilterBar";
import IssueListToolbar from "./IssueListToolbar";
import IssueListTable from "./IssueListTable";
import {
  getAssigneeDisplay,
  getListFromApiResponse,
  mapSystemListItemToOption,
  PRIORITY_ICON_MAP,
  DEFAULT_STATUS_FILTER_OPTIONS,
  DEFAULT_PRIORITY_FILTER_OPTIONS,
  isWorkItemStatusCategoryApiValue,
  issueMatchesWorkItemStatusCategory,
  normalizeWorkItemStatusCategoriesForApi,
  partitionWorkItemListStatusFilter,
  isEpicWorkItemRow,
} from "./issueMappers";
import {
  applyShowEpicsFilterParam,
  applyHideDoneFilterParam,
  WORK_ITEMS_REPORTER_IDS_QUERY_PARAM,
} from "../../Redux/Thunks/projectManagementThunkUtils";
import {
  updateWorkItem,
  deleteWorkItem,
  fetchAllWorkItems,
  fetchMyWorkItems,
  fetchWorkItemsByProjectId,
} from "../../Redux/Thunks/projectManagementThunk";
import { prepareProjectWorkItemsTab } from "../../Redux/Slices/issuesSlice";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import { showError, showSuccess } from "../../utils/toast";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { downloadProjectWorkItemsExport } from "../../utils/downloadProjectWorkItemsExport";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import {
  ISSUES_LISTING_FILTER_DEFAULT,
  WORK_ITEMS_API_PAGE_SIZE,
  normalizeListingFilterIds,
  buildProjectWorkItemsFilterBody,
} from "../Projects/project-detail/issues/issues.mapper";

const defaultStatusText = (status) => (status == null || status === "" ? "N/A" : String(status));

const WORK_ITEM_LISTING_MULTI_KEYS = new Set([
  "client",
  "project",
  "type",
  "status",
  "priority",
  "assignee",
  "reporter",
  "team",
  "participantUserIds",
]);

const WORK_ITEM_LISTING_BOOLEAN_KEYS = new Set(["showEpics", "hideDone"]);

function filterValuesToArray(val) {
  if (val == null || val === "") return [];
  return Array.isArray(val)
    ? val.filter((v) => v != null && String(v).trim() !== "")
    : [String(val)];
}

/** Derive page count from GET work-items `meta` (totalPages, or total + limit). */
function resolveServerTotalPages(meta, fallbackLimit) {
  if (!meta || typeof meta !== "object") return null;
  const tp = Number(meta.totalPages);
  if (Number.isFinite(tp) && tp >= 1) return tp;
  const total = Number(meta.total ?? meta.totalCount ?? meta.total_count);
  const lim = Number(meta.limit ?? fallbackLimit);
  if (Number.isFinite(total) && total > 0 && Number.isFinite(lim) && lim > 0) {
    return Math.max(1, Math.ceil(total / lim));
  }
  return null;
}

/**
 * Same query shape as the Filter button — used for apply + refetch after table mutations
 * so priority (and other) filters stay on the request.
 */
function buildWorkItemsListServerArgs({ projectSlug, filterMode, filter, limit, page = 1 }) {
  const lf = filter && typeof filter === "object" ? filter : {};
  const searchQ = String(lf.search ?? "").trim();
  if (projectSlug) {
    const resolvedLimit = limit ?? WORK_ITEMS_API_PAGE_SIZE;
    const body = { limit: resolvedLimit, page };
    const typeIds = normalizeListingFilterIds(lf.type);
    if (typeIds) body.typeIds = typeIds;
    const { categories, ids } = partitionWorkItemListStatusFilter(lf.status);
    if (categories.length) {
      const norm = normalizeWorkItemStatusCategoriesForApi(categories);
      body.statusCategories = norm.length === 1 ? norm[0] : norm;
    }
    if (ids.length) body.statusIds = ids.length === 1 ? ids[0] : ids;
    const priorityIds = normalizeListingFilterIds(lf.priority);
    if (priorityIds) body.priorityIds = priorityIds;
    const assigneeId = normalizeListingFilterIds(lf.assignee);
    if (assigneeId) body.assigneeId = assigneeId;
    const reporterIdsVal = normalizeListingFilterIds(lf.reporter);
    if (reporterIdsVal) body[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM] = reporterIdsVal;
    if (Array.isArray(lf.team) && lf.team.length > 0) body.teamIds = lf.team;
    if (searchQ) body.search = searchQ;
    applyShowEpicsFilterParam(body, lf.showEpics);
    return { kind: "project", projectId: String(projectSlug), body };
  }
  const listMode = filterMode === "recent" ? "all" : filterMode;
  if (listMode === "all") {
    const params = { page: page ?? 1, limit: limit ?? WORK_ITEMS_API_PAGE_SIZE };
    const proj = normalizeListingFilterIds(lf.project);
    if (proj) params.projectId = proj;
    const cli = normalizeListingFilterIds(lf.client);
    if (cli) params.clientId = cli;
    const tp = normalizeListingFilterIds(lf.type);
    if (tp) params.typeIds = tp;
    const { categories, ids } = partitionWorkItemListStatusFilter(lf.status);
    if (categories.length) {
      const norm = normalizeWorkItemStatusCategoriesForApi(categories);
      params.statusCategories = norm.length === 1 ? norm[0] : norm;
    }
    if (ids.length) params.statusIds = ids.length === 1 ? ids[0] : ids;
    const pr = normalizeListingFilterIds(lf.priority);
    if (pr) params.priorityIds = pr;
    const asg = normalizeListingFilterIds(lf.assignee);
    if (asg) params.assigneeId = asg;
    const reporterIdsAll = normalizeListingFilterIds(lf.reporter);
    if (reporterIdsAll) params[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM] = reporterIdsAll;
    if (searchQ) params.search = searchQ;
    if (Array.isArray(lf.team) && lf.team.length > 0) params.teamIds = lf.team;
    applyShowEpicsFilterParam(params, lf.showEpics);
    applyHideDoneFilterParam(params, lf.hideDone);
    return { kind: "all", params };
  }
  if (listMode === "my") {
    const params = { page: page ?? 1, limit: limit ?? WORK_ITEMS_API_PAGE_SIZE };
    const proj = normalizeListingFilterIds(lf.project);
    if (proj) params.projectId = proj;
    const cli = normalizeListingFilterIds(lf.client);
    if (cli) params.clientId = cli;
    const tp = normalizeListingFilterIds(lf.type);
    if (tp) params.typeIds = tp;
    const { categories, ids } = partitionWorkItemListStatusFilter(lf.status);
    if (categories.length) {
      const norm = normalizeWorkItemStatusCategoriesForApi(categories);
      params.statusCategories = norm.length === 1 ? norm[0] : norm;
    }
    if (ids.length) params.statusIds = ids.length === 1 ? ids[0] : ids;
    const pr = normalizeListingFilterIds(lf.priority);
    if (pr) params.priorityIds = pr;
    if (lf.dueBefore) params.dueBefore = lf.dueBefore;
    if (lf.dueAfter) params.dueAfter = lf.dueAfter;
    const reporterIdsMy = normalizeListingFilterIds(lf.reporter);
    if (reporterIdsMy) params[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM] = reporterIdsMy;
    if (searchQ) params.search = searchQ;
    if (Array.isArray(lf.team) && lf.team.length > 0) params.teamIds = lf.team;
    applyShowEpicsFilterParam(params, lf.showEpics);
    applyHideDoneFilterParam(params, lf.hideDone);
    return { kind: "my", params };
  }
  return null;
}

function dispatchWorkItemsListFetch(dispatch, args) {
  if (!args) return Promise.resolve();
  if (args.kind === "project") {
    return dispatch(fetchWorkItemsByProjectId({ projectId: args.projectId, body: args.body }));
  }
  if (args.kind === "all") {
    return dispatch(fetchAllWorkItems(args.params));
  }
  if (args.kind === "my") return dispatch(fetchMyWorkItems(args.params));
  return Promise.resolve();
}

const IssueList = ({
  issuesData,
  loading = false,
  currentPage = 1,
  setCurrentPage = () => {},
  getStatusText = defaultStatusText,
  listingFilter = {},
  handleFilterChange = () => {},
  handleFilterReset = () => {},
  assigneeOptions = [],
  filterMode = "all",
  projectName = null,
  projectSlug = null,
  onIssueCreated = null,
  projectsData = null,
  clientsData = null,
  typeOptions = null,
  statusOptions: statusOptionsProp = null,
  /** Status filter dropdown (category codes). Defaults to coarse categories when omitted. */
  statusFilterOptions = null,
  priorityOptions = null,
  /** Increment when parent commits filters from URL (e.g. dashboard deep link) so appliedFilter matches draft. */
  syncAppliedFromUrlToken = 0,
  /** Project Work Items: persist last-applied filters to parent on Filter click (modal refetch, etc.). */
  onCommitListingFilter = null,
  /** Open work item in modal: `(issueId, projectId)`; project id from row when not in project route. */
  onOpenWorkItemDetail,
  /** Issues.jsx: register `() => refetch with current applied filters` for work-item modal save. */
  registerGlobalListRefetch = null,
  /** When true: hides the Filter button in WorkItemsFilterBar (changes apply via debounce in parent). */
  liveMode = false,
  // ── Saved filter props — passed through to WorkItemsFilterBar ──
  isDirty = false,
  activeSavedSearchId = null,
  saving = false,
  onSave,
  onSaveAs,
  /** When false, Save As is rendered on SavedSearchBar (global Issues). */
  showSaveAs = true,
  /** Per-tab show epics toggle (All / My) — not stored in listingFilter. */
  showEpics: showEpicsProp,
  onShowEpicsChange = null,
}) => {
  const serverBackedWorkItemsList = !projectSlug && (filterMode === "all" || filterMode === "my");
  const showEpicsControlled = typeof showEpicsProp === "boolean";

  const mergeListingIntoApplied = (lf) => {
    const merged = {
      ...ISSUES_LISTING_FILTER_DEFAULT,
      ...(lf && typeof lf === "object" ? lf : {}),
    };
    if (showEpicsControlled) delete merged.showEpics;
    return merged;
  };
  const { pageSize, setPageSize } = useListPageSize(WORK_ITEMS_LIST_PAGE_SIZE_STORAGE_KEY);

  const buildEmptyFilter = (template) => {
    const t = template && typeof template === "object" ? template : {};
    const keys = new Set([...Object.keys(ISSUES_LISTING_FILTER_DEFAULT), ...Object.keys(t)]);
    return Object.fromEntries(
      [...keys].map((k) => {
        if (WORK_ITEM_LISTING_MULTI_KEYS.has(k)) return [k, []];
        if (WORK_ITEM_LISTING_BOOLEAN_KEYS.has(k)) {
          return [k, ISSUES_LISTING_FILTER_DEFAULT[k] ?? false];
        }
        return [k, ""];
      })
    );
  };

  const [appliedFilter, setAppliedFilter] = useState(() => mergeListingIntoApplied(listingFilter));

  const [projectFilterDraft, setProjectFilterDraft] = useState(() =>
    projectSlug ? mergeListingIntoApplied(listingFilter) : {}
  );

  const showEpicsOn = showEpicsControlled
    ? showEpicsProp === true
    : appliedFilter?.showEpics === true;

  const effectiveListFilter = useMemo(
    () => ({ ...appliedFilter, showEpics: showEpicsOn }),
    [appliedFilter, showEpicsOn]
  );

  const appliedFilterRef = useRef(effectiveListFilter);
  appliedFilterRef.current = effectiveListFilter;

  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  const listingFilterRef = useRef(listingFilter);
  listingFilterRef.current = listingFilter;

  // Sync draft → applied only when workspace changes. For project Work Items, dropdown edits
  // use `projectFilterDraft` so parent `listingFilter` is not updated until Filter (avoids refetch on select).
  useEffect(() => {
    const next = mergeListingIntoApplied(listingFilterRef.current);
    setAppliedFilter(next);
    if (projectSlug) setProjectFilterDraft(next);
  }, [projectSlug, filterMode, showEpicsControlled]);

  useEffect(() => {
    if (!syncAppliedFromUrlToken) return;
    const next = mergeListingIntoApplied(listingFilterRef.current);
    setAppliedFilter(next);
    if (projectSlug) setProjectFilterDraft(next);
  }, [syncAppliedFromUrlToken, projectSlug, showEpicsControlled]);

  const handleProjectFilterDraftChange = (e) => {
    const { name, value } = e.target;
    setProjectFilterDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterResetWrapped = () => {
    const template = projectSlug ? projectFilterDraft : listingFilter;
    const empty = buildEmptyFilter(template);
    setAppliedFilter(empty);
    if (projectSlug) setProjectFilterDraft(empty);
    handleFilterReset();
  };

  const navigate = useNavigate();
  const location = useLocation();
  const currentProjectTab = new URLSearchParams(location.search).get("tab");
  const dispatch = useDispatch();
  const { can } = usePermissions();
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  /** First fetch after opening this project’s Work Items (or new `projectSlug`) — clear Kanban slice before list GET. */
  const projectWorkItemsListSessionRef = useRef(null);

  useEffect(() => {
    if (!projectSlug) {
      projectWorkItemsListSessionRef.current = null;
      return undefined;
    }
    if (projectWorkItemsListSessionRef.current !== projectSlug) {
      dispatch(prepareProjectWorkItemsTab());
      projectWorkItemsListSessionRef.current = projectSlug;
    }
    dispatchWorkItemsListFetch(
      dispatch,
      buildWorkItemsListServerArgs({
        projectSlug,
        filterMode,
        filter: effectiveListFilter,
        limit: pageSize,
        page: currentPage,
      })
    );
    return undefined;
  }, [dispatch, projectSlug, currentPage, pageSize, filterMode, effectiveListFilter]);

  useEffect(() => {
    if (!serverBackedWorkItemsList) return undefined;
    dispatchWorkItemsListFetch(
      dispatch,
      buildWorkItemsListServerArgs({
        projectSlug: null,
        filterMode,
        filter: effectiveListFilter,
        limit: pageSize,
        page: currentPage,
      })
    );
    return undefined;
  }, [dispatch, serverBackedWorkItemsList, filterMode, effectiveListFilter, currentPage, pageSize]);

  useEffect(() => {
    if (typeof registerGlobalListRefetch !== "function") return undefined;
    if (!serverBackedWorkItemsList) {
      registerGlobalListRefetch(null);
      return undefined;
    }
    const refetch = () =>
      dispatchWorkItemsListFetch(
        dispatch,
        buildWorkItemsListServerArgs({
          projectSlug: null,
          filterMode,
          filter: appliedFilterRef.current,
          limit: pageSize,
          page: currentPageRef.current,
        })
      ).unwrap();
    registerGlobalListRefetch(refetch);
    return () => {
      registerGlobalListRefetch(null);
    };
  }, [dispatch, registerGlobalListRefetch, serverBackedWorkItemsList, filterMode, pageSize]);

  useEffect(() => {
    setSelectedIssues(new Set());
  }, [currentPage, projectSlug]);

  const canCreateWorkItem = can(PERMISSION_KEYS.WORK_ITEMS_CREATE);
  const canEditWorkItem = can(PERMISSION_KEYS.WORK_ITEMS_EDIT);
  const canDeleteWorkItem = can(PERMISSION_KEYS.WORK_ITEMS_DELETE);

  // Remove status update option from All Work Items + My Issues lists.
  // (Still allow in project-specific boards/details.)
  const allowStatusUpdate = filterMode !== "all" && filterMode !== "my";
  const exportDropdownRef = useRef(null);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Selection state
  const [selectedIssues, setSelectedIssues] = useState(new Set());

  // Inline editing state
  const [editingField, setEditingField] = useState(null); // { issueId, field: 'status' | 'priority' | 'assignee' }
  const [prioritySavingIssueId, setPrioritySavingIssueId] = useState(null);
  const [assigneeSavingIssueId, setAssigneeSavingIssueId] = useState(null);

  // Status and priority options for inline edit (use API list when available)
  const statusOptions = statusOptionsProp?.length
    ? statusOptionsProp
    : DEFAULT_STATUS_FILTER_OPTIONS;
  const [priorityOptionsForDropdown, setPriorityOptionsForDropdown] = useState([]);

  // Load PRIORITY system list for inline priority editing (with IDs)
  useEffect(() => {
    dispatch(fetchSystemList({ listType: "PRIORITY", context: "issue-list-priority" }))
      .unwrap()
      .then((raw) => {
        const list = getListFromApiResponse(raw);
        const options = list.map((item) => mapSystemListItemToOption(item, PRIORITY_ICON_MAP));
        setPriorityOptionsForDropdown(options);
      })
      .catch(() => {
        setPriorityOptionsForDropdown([]);
      });
  }, [dispatch]);

  // Prefer IssueList fetch; else parent-provided options (AllIssues / ProjectsIssues load the same list).
  // Avoid string-only fallbacks until a system list exists — those break PATCH { priorityId: uuid }.
  const resolvedPriorityDropdownOptions = useMemo(() => {
    if (priorityOptionsForDropdown.length > 0) return priorityOptionsForDropdown;
    if (Array.isArray(priorityOptions) && priorityOptions.length > 0) return priorityOptions;
    return [];
  }, [priorityOptionsForDropdown, priorityOptions]);

  const fallbackPriorityOptionsForDropdown =
    resolvedPriorityDropdownOptions.length > 0
      ? resolvedPriorityDropdownOptions
      : DEFAULT_PRIORITY_FILTER_OPTIONS;

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [issueToDelete, setIssueToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
      if (editingField) {
        const isClickInDropdown =
          event.target.closest(".relative") ||
          event.target.closest("input[readonly]") ||
          event.target.closest('[role="listbox"]') ||
          event.target.closest("[data-dropdown-portal]");
        if (!isClickInDropdown) {
          setEditingField(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingField]);

  // Selection handlers
  const handleSelectIssue = (issueId, checked) => {
    const newSelected = new Set(selectedIssues);
    if (checked) {
      newSelected.add(issueId);
    } else {
      newSelected.delete(issueId);
    }
    setSelectedIssues(newSelected);
  };

  // Inline editing handlers
  const handleDoubleClick = (issueId, field) => {
    if (!canEditWorkItem) {
      setDeniedPermissionKey(PERMISSION_KEYS.WORK_ITEMS_EDIT);
      return;
    }
    setEditingField({ issueId, field });
  };

  const handleInlineEdit = async (issueId, field, value) => {
    if (!canEditWorkItem) return;
    const issue = issuesList.find((i) => i.id === issueId);
    if (!issue) {
      setEditingField(null);
      return;
    }

    try {
      if (field === "priority") {
        const projectIdForUrl =
          projectSlug ?? issue.projectId ?? issue.project?.id ?? issue.project_id ?? null;
        if (!projectIdForUrl) {
          showError("Cannot update priority: this work item has no project id.");
          return;
        }

        // Match by option value (UUID) first, then by label (legacy / mis-mapped dropdown value)
        const selected =
          fallbackPriorityOptionsForDropdown.find((opt) => {
            if (typeof opt === "string") {
              return String(opt) === String(value);
            }
            return String(opt.value ?? opt.id) === String(value);
          }) ??
          fallbackPriorityOptionsForDropdown.find((opt) => {
            if (typeof opt === "string") {
              return false;
            }
            return String(opt.label) === String(value);
          });

        let priorityId = null;
        if (selected != null) {
          if (typeof selected === "string") {
            priorityId = selected;
          } else {
            priorityId = selected.value ?? selected.id ?? null;
          }
        }

        if (priorityId == null || String(priorityId).trim() === "") {
          showError(
            "Could not resolve priority id. Wait for the priority list to load, then try again."
          );
          return;
        }

        setPrioritySavingIssueId(issueId);
        try {
          await dispatch(
            updateWorkItem({
              projectId: projectIdForUrl,
              workItemId: issueId,
              body: {
                version: issue.version ?? 1,
                priorityId: String(priorityId).trim(),
              },
            })
          ).unwrap();
          showSuccess("Priority updated successfully");

          dispatchWorkItemsListFetch(
            dispatch,
            buildWorkItemsListServerArgs({
              projectSlug,
              filterMode,
              filter: appliedFilterRef.current,
              limit: pageSize,
              page: currentPage,
            })
          );
        } catch (err) {
          showError(getApiErrorMessage(err, "Failed to update priority"));
        } finally {
          setPrioritySavingIssueId(null);
        }
      } else if (field === "assignee") {
        const projectIdForUrl =
          projectSlug ?? issue.projectId ?? issue.project?.id ?? issue.project_id ?? null;
        if (!projectIdForUrl) {
          showError("Cannot update assignee: this work item has no project id.");
          return;
        }

        const selected =
          assigneeOptions.find((opt) => {
            if (typeof opt === "string") return String(opt) === String(value);
            return String(opt?.value ?? opt?.id ?? "") === String(value);
          }) ??
          assigneeOptions.find((opt) => {
            if (typeof opt === "string") return false;
            return String(opt?.label ?? "") === String(value);
          });

        let assigneeId = null;
        if (selected != null) {
          if (typeof selected === "string") {
            assigneeId = selected;
          } else {
            assigneeId = selected.value ?? selected.id ?? null;
          }
        }

        if (assigneeId == null || String(assigneeId).trim() === "") {
          showError(
            "Could not resolve assignee id. Wait for users to load, then select an assignee from the list."
          );
          return;
        }

        setAssigneeSavingIssueId(issueId);
        try {
          await dispatch(
            updateWorkItem({
              projectId: projectIdForUrl,
              workItemId: issueId,
              body: {
                version: issue.version ?? 1,
                assigneeId: String(assigneeId).trim(),
              },
            })
          ).unwrap();
          showSuccess("Assignee updated successfully");

          dispatchWorkItemsListFetch(
            dispatch,
            buildWorkItemsListServerArgs({
              projectSlug,
              filterMode,
              filter: appliedFilterRef.current,
              limit: pageSize,
              page: currentPage,
            })
          );
        } catch (err) {
          showError(getApiErrorMessage(err, "Failed to update assignee"));
        } finally {
          setAssigneeSavingIssueId(null);
        }
      } else if (field === "status") {
        const projectIdForUrl =
          projectSlug ?? issue.projectId ?? issue.project?.id ?? issue.project_id ?? null;
        if (!projectIdForUrl) {
          showError("Cannot update status: this work item has no project id.");
          return;
        }

        const statusId = value;
        if (!statusId) return;

        await dispatch(
          updateWorkItem({
            projectId: projectIdForUrl,
            workItemId: issueId,
            body: {
              version: issue.version ?? 1,
              statusId,
            },
          })
        ).unwrap();
        showSuccess("Status updated successfully");

        dispatchWorkItemsListFetch(
          dispatch,
          buildWorkItemsListServerArgs({
            projectSlug,
            filterMode,
            filter: appliedFilterRef.current,
            limit: pageSize,
            page: currentPage,
          })
        );
      }
    } finally {
      setEditingField(null);
    }
  };

  const handleDeleteIssueClick = (issue, e) => {
    e.stopPropagation();
    if (!canDeleteWorkItem) {
      setDeniedPermissionKey(PERMISSION_KEYS.WORK_ITEMS_DELETE);
      return;
    }
    setIssueToDelete(issue);
  };

  const handleConfirmDeleteIssue = async () => {
    if (!issueToDelete?.id) return;
    setDeleteLoading(true);
    try {
      const projectIdForUrl = projectSlug ?? issueToDelete.projectId ?? issueToDelete.project?.id;
      if (!projectIdForUrl) {
        throw new Error("Project ID is required to delete this work item.");
      }

      await dispatch(
        deleteWorkItem({
          projectId: projectIdForUrl,
          workItemId: issueToDelete.id,
        })
      ).unwrap();
      showSuccess("Work item deleted successfully");

      await dispatchWorkItemsListFetch(
        dispatch,
        buildWorkItemsListServerArgs({
          projectSlug,
          filterMode,
          filter: appliedFilterRef.current,
          limit: pageSize,
          page: currentPage,
        })
      );
      setIssueToDelete(null);
    } catch (err) {
      const msg = (typeof err === "string" ? err : err?.message) || "Failed to delete work item";
      showError(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Apply filters (commit draft → applied + fetch where needed)
  const applyFilters = () => {
    setCurrentPage(1);
    const source =
      projectSlug && projectFilterDraft && typeof projectFilterDraft === "object"
        ? projectFilterDraft
        : listingFilter;
    const nextApplied = {
      ...ISSUES_LISTING_FILTER_DEFAULT,
      ...(source && typeof source === "object" ? source : {}),
    };
    setAppliedFilter(nextApplied);
    if (projectSlug && typeof onCommitListingFilter === "function") {
      onCommitListingFilter(nextApplied);
    }
  };

  const showEpicsToggleVisible =
    filterMode === "all" || filterMode === "my" || Boolean(projectSlug);

  const handleShowEpicsChange = (checked) => {
    setCurrentPage(1);
    if (showEpicsControlled) {
      onShowEpicsChange?.(checked);
      return;
    }
    setAppliedFilter((prev) => ({ ...prev, showEpics: checked }));
    if (projectSlug) {
      setProjectFilterDraft((prev) => ({ ...prev, showEpics: checked }));
      if (typeof onCommitListingFilter === "function") {
        onCommitListingFilter({ ...appliedFilterRef.current, showEpics: checked });
      }
    } else if (typeof handleFilterChange === "function") {
      handleFilterChange({ target: { name: "showEpics", value: checked } });
    }
  };

  // "Hide done" — All Work Items / My Work Items tabs only; server-side (see applyHideDoneFilterParam).
  const hideDoneToggleVisible = !projectSlug && (filterMode === "all" || filterMode === "my");
  const hideDoneOn = appliedFilter?.hideDone === true;

  const handleHideDoneChange = (checked) => {
    setCurrentPage(1);
    setAppliedFilter((prev) => ({ ...prev, hideDone: checked }));
    if (typeof handleFilterChange === "function") {
      handleFilterChange({ target: { name: "hideDone", value: checked } });
    }
  };

  // Filter issues based on search term and filters (safe when list missing)
  const issuesList = issuesData?.list ?? [];
  const appliedSearchDisplay = String(appliedFilter?.search ?? "").trim();
  const filteredIssues = issuesList.filter((issue) => {
    // Prefer client-side filtering when not in a project-specific view.
    // This provides reliable filtering even if the backend ignores some params.
    const isServerFiltered = !!projectSlug;
    // All Issues / My Issues: client, project, and team facets are sent on fetchAllWorkItems / fetchMyWorkItems.
    // Rows from the API may omit clientId / teamId / nested shapes — client-side re-filter would hide every row.
    const skipClientSideServerBackedListingFacets =
      !projectSlug && (filterMode === "all" || filterMode === "my");
    const skipClientSearchForServerList = skipClientSideServerBackedListingFacets;
    const skipClientSearchKeyword =
      skipClientSearchForServerList || (!!projectSlug && appliedSearchDisplay.length > 0);

    // Project filter (if projectName is provided from parent)
    const matchesProject = !projectName || issue.projectName === projectName;

    // Filters from dropdowns (global + my view)
    const issueProjectId = issue.projectId ?? issue.project?.id ?? issue.project?.projectId ?? null;
    const issueClientId = issue.clientId ?? issue.client?.id ?? issue.client?.clientId ?? null;

    const projectFilterVals = filterValuesToArray(appliedFilter?.project);
    const matchesProjectByFilter =
      skipClientSideServerBackedListingFacets ||
      projectFilterVals.length === 0 ||
      (issueProjectId != null &&
        projectFilterVals.some((p) => String(issueProjectId) === String(p)));

    const clientFilterVals = filterValuesToArray(appliedFilter?.client);
    const matchesClientByFilter =
      skipClientSideServerBackedListingFacets ||
      clientFilterVals.length === 0 ||
      (issueClientId != null && clientFilterVals.some((c) => String(issueClientId) === String(c)));

    // Search: server-backed lists pass `search` on the API — do not re-filter client-side.
    const matchesSearch =
      skipClientSearchKeyword ||
      !appliedSearchDisplay ||
      issue.title?.toLowerCase().includes(appliedSearchDisplay.toLowerCase()) ||
      issue.description?.toLowerCase().includes(appliedSearchDisplay.toLowerCase()) ||
      issue.issueKey?.toLowerCase().includes(appliedSearchDisplay.toLowerCase()) ||
      String(issue.clientName ?? "")
        .toLowerCase()
        .includes(appliedSearchDisplay.toLowerCase());

    // Type filter — dropdown value is usually a system-list id; row `type` is a display name.
    const typeFilterVals = filterValuesToArray(appliedFilter?.type);
    const matchesType =
      isServerFiltered ||
      typeFilterVals.length === 0 ||
      typeFilterVals.some(
        (tf) => issue.type === tf || (issue.typeId != null && String(issue.typeId) === String(tf))
      );

    // Status / priority — UUID vs display string; All Issues may use `statusCategories` (NEW, TO_TESTING, ON_HOLD, …).
    const statusFilterVals = filterValuesToArray(appliedFilter?.status);
    const matchesStatus =
      isServerFiltered ||
      statusFilterVals.length === 0 ||
      statusFilterVals.some(
        (statusFilterVal) =>
          issue.status === statusFilterVal ||
          (issue.statusId != null && String(issue.statusId) === String(statusFilterVal)) ||
          (isWorkItemStatusCategoryApiValue(statusFilterVal) &&
            issueMatchesWorkItemStatusCategory(issue, statusFilterVal))
      );

    const priorityFilterVals = filterValuesToArray(appliedFilter?.priority);
    const matchesPriority =
      isServerFiltered ||
      priorityFilterVals.length === 0 ||
      priorityFilterVals.some(
        (pf) =>
          issue.priority === pf ||
          (issue.priorityId != null && String(issue.priorityId) === String(pf))
      );

    // Assignee filter: dropdown value is user id (UUID); assignee on row is often a display string
    // after normalizeWorkItemFromApi — compare id to filter, or display name for legacy.
    const issueAssigneeDisplay = getAssigneeDisplay(issue.assignee) || "";
    const issueAssigneeId = issue.assigneeId ?? null;
    const assigneeFilterVals = filterValuesToArray(appliedFilter?.assignee);
    const matchesAssignee =
      filterMode === "my" ||
      isServerFiltered ||
      assigneeFilterVals.length === 0 ||
      assigneeFilterVals.some(
        (af) =>
          issueAssigneeDisplay === af ||
          (issueAssigneeId != null && String(issueAssigneeId) === String(af))
      );

    // Team filter — global All/My lists pass teamIds on the fetch; do not drop rows when rows omit team fields.
    const matchesTeam =
      skipClientSideServerBackedListingFacets ||
      isServerFiltered ||
      !appliedFilter?.team ||
      (Array.isArray(appliedFilter.team) && appliedFilter.team.length === 0) ||
      (Array.isArray(appliedFilter.team)
        ? appliedFilter.team.includes(issue.teamId ?? issue.team?.id)
        : String(issue.teamId ?? issue.team?.id) === String(appliedFilter.team));

    const matchesShowEpics = showEpicsOn || !isEpicWorkItemRow(issue, typeOptions);

    const matchesHideDone =
      !hideDoneToggleVisible || !hideDoneOn || !issueMatchesWorkItemStatusCategory(issue, "CLOSED");

    return (
      matchesProject &&
      matchesProjectByFilter &&
      matchesClientByFilter &&
      matchesSearch &&
      matchesType &&
      matchesStatus &&
      matchesPriority &&
      matchesAssignee &&
      matchesTeam &&
      matchesShowEpics &&
      matchesHideDone
    );
  });

  let issuesToSort = [...filteredIssues];
  if (filterMode === "recent") {
    issuesToSort.sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB - dateA;
    });
  }

  // Sorting
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(issuesToSort, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  // Reset to page 1 when sorting changes
  useEffect(() => {
    if (sortKey) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortDirection]);

  // Pagination — project + All/My work items use server `meta` when present; other modes slice client-side.
  const listMeta = issuesData?.meta;
  const serverTotalPages = resolveServerTotalPages(listMeta, pageSize);
  const useServerPagination =
    serverTotalPages != null &&
    serverTotalPages >= 1 &&
    (Boolean(projectSlug) || serverBackedWorkItemsList);

  const clientTotalPages = Math.max(1, Math.ceil(sortedData.length / pageSize) || 1);
  const totalPages = useServerPagination ? serverTotalPages : clientTotalPages;

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedIssues = useServerPagination ? sortedData : sortedData.slice(startIndex, endIndex);

  useEffect(() => {
    if (!projectSlug && !serverBackedWorkItemsList) return undefined;
    const stp = resolveServerTotalPages(issuesData?.meta, pageSize);
    if (stp != null && stp >= 1 && currentPage > stp) {
      setCurrentPage(stp);
    }
    return undefined;
  }, [projectSlug, serverBackedWorkItemsList, issuesData?.meta, currentPage, setCurrentPage]);

  // Selection state calculations (after paginatedIssues is defined)
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIssues(new Set(paginatedIssues.map((issue) => issue.id)));
    } else {
      setSelectedIssues(new Set());
    }
  };

  const isAllSelected =
    paginatedIssues.length > 0 && paginatedIssues.every((issue) => selectedIssues.has(issue.id));
  const isIndeterminate = selectedIssues.size > 0 && selectedIssues.size < paginatedIssues.length;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const runProjectExport = async (format) => {
    if (!projectSlug) return;
    setExportLoading(true);
    try {
      await downloadProjectWorkItemsExport(
        projectSlug,
        format,
        buildProjectWorkItemsFilterBody(effectiveListFilter)
      );
      setExportDropdownOpen(false);
    } catch (e) {
      showError(e?.message || "Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="clients-page-content !bg-transparent">
      <div className="w-full">
        <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
          <WorkItemsFilterBar
            listingFilter={projectSlug ? projectFilterDraft : listingFilter}
            handleFilterChange={projectSlug ? handleProjectFilterDraftChange : handleFilterChange}
            handleFilterReset={handleFilterResetWrapped}
            applyFilters={applyFilters}
            assigneeOptions={assigneeOptions}
            projectSlug={projectSlug}
            projectsData={projectsData}
            clientsData={clientsData}
            typeOptions={typeOptions}
            statusOptions={statusOptionsProp}
            statusFilterOptions={statusFilterOptions}
            priorityOptions={priorityOptions}
            filterMode={filterMode}
            liveMode={liveMode}
          />

          <IssueListToolbar
            exportDropdownRef={exportDropdownRef}
            exportDropdownOpen={exportDropdownOpen}
            setExportDropdownOpen={setExportDropdownOpen}
            showProjectExport={Boolean(projectSlug)}
            exportLoading={exportLoading}
            onExportCsv={() => runProjectExport("csv")}
            onExportXlsx={() => runProjectExport("xlsx")}
            onCreateClick={() => {
              if (!canCreateWorkItem) {
                setDeniedPermissionKey(PERMISSION_KEYS.WORK_ITEMS_CREATE);
                return;
              }
              setOpenCreateModal(true);
            }}
            isDirty={isDirty}
            activeSavedSearchId={activeSavedSearchId}
            saving={saving}
            onSave={onSave}
            onSaveAs={onSaveAs}
            showSaveAs={showSaveAs}
            showCreate={canCreateWorkItem}
            showEpicsToggle={showEpicsToggleVisible}
            showEpicsOn={showEpicsOn}
            onShowEpicsChange={handleShowEpicsChange}
            hideDoneToggle={hideDoneToggleVisible}
            hideDoneOn={hideDoneOn}
            onHideDoneChange={handleHideDoneChange}
          />

          <IssueListTable
            paginatedIssues={paginatedIssues}
            searchTerm={appliedSearchDisplay}
            sortKey={sortKey}
            sortDirection={sortDirection}
            handleSort={handleSort}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            onSelectAll={handleSelectAll}
            selectedIssues={selectedIssues}
            onSelectIssue={handleSelectIssue}
            projectSlug={projectSlug}
            currentProjectTab={currentProjectTab}
            navigate={navigate}
            onOpenWorkItemDetail={onOpenWorkItemDetail}
            workItemTypeOptions={typeOptions}
            editingField={editingField}
            setEditingField={setEditingField}
            onDoubleClick={handleDoubleClick}
            onInlineEdit={handleInlineEdit}
            priorityOptionsForDropdown={fallbackPriorityOptionsForDropdown}
            statusOptions={statusOptions}
            assigneeOptions={assigneeOptions}
            allowStatusUpdate={allowStatusUpdate}
            getStatusText={getStatusText}
            onDeleteClick={handleDeleteIssueClick}
            canEditWorkItems={canEditWorkItem}
            canDeleteWorkItems={canDeleteWorkItem}
            showActionsColumn={canDeleteWorkItem}
            prioritySavingIssueId={prioritySavingIssueId}
            assigneeSavingIssueId={assigneeSavingIssueId}
            constrainWorkColumn={serverBackedWorkItemsList}
          />

          {(paginatedIssues.length > 0 || sortedData.length > 0) && (
            <ListPaginationBar
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </ContentLoader>

        <CreateModal
          open={openCreateModal}
          onClose={() => setOpenCreateModal(false)}
          submitDisabled={createSubmitting}
        >
          <CreateTaskForm
            projectsList={projectsData?.list}
            clientsList={clientsData?.list}
            currentProjectId={projectSlug}
            onSuccess={() => {
              setOpenCreateModal(false);
              if (serverBackedWorkItemsList) {
                dispatchWorkItemsListFetch(
                  dispatch,
                  buildWorkItemsListServerArgs({
                    projectSlug: null,
                    filterMode,
                    filter: appliedFilterRef.current,
                    limit: pageSize,
                    page: 1,
                  })
                );
                setCurrentPage(1);
              }
              if (!serverBackedWorkItemsList) {
                onIssueCreated?.();
              }
            }}
            onSubmittingChange={setCreateSubmitting}
          />
        </CreateModal>

        <DeleteConfirmModal
          isOpen={!!issueToDelete}
          onClose={() => setIssueToDelete(null)}
          onConfirm={handleConfirmDeleteIssue}
          title="Delete work item"
          itemName={issueToDelete?.title ?? issueToDelete?.name}
          loading={deleteLoading}
        />

        <PermissionDeniedModal
          isOpen={deniedPermissionKey != null}
          onClose={() => setDeniedPermissionKey(null)}
          permissionKey={deniedPermissionKey}
        />
      </div>
    </div>
  );
};

export default IssueList;
